/**
 * Pagina lista utenti del tenant.
 *
 * Visibile solo ad admin e super_admin.
 * Endpoint: GET /api/v1/utenti, POST /api/v1/utenti
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UtenteRow {
  id: string;
  email: string;
  nome: string;
  cognome: string;
  stato: string;
  ruolo: string | null;
  ultimo_accesso: string | null;
  created_at: string;
}

const RUOLO_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  responsabile: 'Responsabile',
  contabile: 'Contabile',
  magazziniere: 'Magazziniere',
  cassiere: 'Cassiere',
  acquisti: 'Acquisti',
  operatore: 'Operatore',
  segretaria: 'Segretaria',
};

const ruoloLabel = (r: string | null): string => {
  if (!r) return '-';
  return RUOLO_LABELS[r] ?? r;
};

const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const EMPTY_FORM = {
  email: '',
  nome: '',
  cognome: '',
  ruolo: 'operatore',
  stato: 'attivo',
  codice_fiscale: '',
  data_nascita: '',
  telefono: '',
  indirizzo: '',
  civico: '',
  cap: '',
  citta: '',
  provincia: '',
  note: '',
};

const UtentiList: React.FC = () => {
  const navigate = useNavigate();
  const [utenti, setUtenti] = useState<UtenteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [stato, setStato] = useState('__all__');
  const [ruolo, setRuolo] = useState('__all__');

  /* Lookup per i dropdown */
  const [ruoliDisponibili, setRuoliDisponibili] = useState<string[]>([]);
  const [statiDisponibili, setStatiDisponibili] = useState<string[]>([]);

  /* Form nuovo utente */
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [passwordTemp, setPasswordTemp] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const token = localStorage.getItem('token') ?? '';

  const setField = (k: keyof typeof EMPTY_FORM, v: string) =>
    setFormData((p) => ({ ...p, [k]: v }));

  const caricaUtenti = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (stato !== '__all__') params.set('stato', stato);
      if (ruolo !== '__all__') params.set('ruolo', ruolo);

      const res = await fetch(`/api/v1/utenti?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setUtenti(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [q, stato, ruolo, token]);

  useEffect(() => {
    void caricaUtenti();
  }, [caricaUtenti]);

  // Lookup ruoli + stati per i dropdown (endpoint dedicati admin).
  useEffect(() => {
    const fetchLookup = async (url: string, set: (v: string[]) => void) => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) set(await res.json());
      } catch {
        /* ignore */
      }
    };
    void fetchLookup('/api/v1/admin/ruoli', setRuoliDisponibili);
    void fetchLookup('/api/v1/admin/stati-utente', setStatiDisponibili);
  }, [token]);

  const salvaUtente = async () => {
    setSaving(true);
    setError(null);
    setPasswordTemp(null);
    setSuccessMsg(null);
    try {
      // Payload: obbligatori + opzionali non vuoti (i vuoti restano null a DB).
      const payload: Record<string, string> = {
        email: formData.email.trim(),
        nome: formData.nome.trim(),
        cognome: formData.cognome.trim(),
        ruolo: formData.ruolo,
        stato: formData.stato,
      };
      const opzionali: (keyof typeof EMPTY_FORM)[] = [
        'codice_fiscale', 'data_nascita', 'telefono', 'indirizzo',
        'civico', 'cap', 'citta', 'provincia', 'note',
      ];
      for (const k of opzionali) {
        const v = formData[k].trim();
        if (v) payload[k] = v;
      }

      const res = await fetch('/api/v1/utenti', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Pydantic 422 restituisce detail come array: normalizzo in messaggio.
        let msg: string;
        if (Array.isArray(err.detail)) {
          msg = err.detail
            .map((d: { loc?: string[]; msg?: string }) =>
              `${d.loc?.slice(1).join('.') ?? ''}: ${d.msg ?? ''}`.trim())
            .join('; ');
        } else {
          msg = err.detail || `Errore ${res.status}`;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      if (data.password_temporanea) setPasswordTemp(data.password_temporanea);
      setSuccessMsg(`Utente ${data.nome} ${data.cognome} creato correttamente.`);
      setFormData({ ...EMPTY_FORM });
      setShowForm(false);
      void caricaUtenti();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso: string | null): string => {
    if (!iso) return 'Mai';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Utenti
        </h1>
        <Button onClick={() => { setShowForm(!showForm); setPasswordTemp(null); setSuccessMsg(null); }}>
          {showForm ? 'Annulla' : 'Nuovo utente'}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="rounded-md bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400">
          {successMsg}
        </div>
      )}

      {/* Password temporanea (mostrata una sola volta) */}
      {passwordTemp && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium">Password temporanea del nuovo utente:</p>
            <code className="mt-1 block rounded bg-muted px-3 py-2 font-mono text-sm">
              {passwordTemp}
            </code>
            <p className="mt-2 text-xs text-muted-foreground">
              Comunicala all'utente: non sara piu visibile dopo aver lasciato questa pagina.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Form nuovo utente */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nuovo utente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Identita' */}
            <div>
              <p className="mb-2 text-sm font-medium text-muted-foreground">Identita'</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setField('email', e.target.value)} />
                </div>
                <div>
                  <Label>Nome *</Label>
                  <Input value={formData.nome} onChange={(e) => setField('nome', e.target.value)} />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input value={formData.cognome} onChange={(e) => setField('cognome', e.target.value)} />
                </div>
                <div>
                  <Label>Codice fiscale</Label>
                  <Input value={formData.codice_fiscale} onChange={(e) => setField('codice_fiscale', e.target.value)} />
                </div>
                <div>
                  <Label>Data di nascita</Label>
                  <Input type="date" value={formData.data_nascita} onChange={(e) => setField('data_nascita', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Contatti e indirizzo */}
            <div>
              <p className="mb-2 text-sm font-medium text-muted-foreground">Contatti e indirizzo</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label>Telefono</Label>
                  <Input value={formData.telefono} onChange={(e) => setField('telefono', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Indirizzo</Label>
                  <Input value={formData.indirizzo} onChange={(e) => setField('indirizzo', e.target.value)} />
                </div>
                <div>
                  <Label>Civico</Label>
                  <Input value={formData.civico} onChange={(e) => setField('civico', e.target.value)} />
                </div>
                <div>
                  <Label>CAP</Label>
                  <Input value={formData.cap} onChange={(e) => setField('cap', e.target.value)} />
                </div>
                <div>
                  <Label>Citta'</Label>
                  <Input value={formData.citta} onChange={(e) => setField('citta', e.target.value)} />
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input value={formData.provincia} onChange={(e) => setField('provincia', e.target.value)} placeholder="es. CS" />
                </div>
              </div>
            </div>

            {/* Accesso */}
            <div>
              <p className="mb-2 text-sm font-medium text-muted-foreground">Accesso</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label>Ruolo *</Label>
                  <Select value={formData.ruolo} onValueChange={(v) => setField('ruolo', v)}>
                    <SelectTrigger><SelectValue placeholder="Ruolo" /></SelectTrigger>
                    <SelectContent>
                      {ruoliDisponibili.map((r) => (
                        <SelectItem key={r} value={r}>{ruoloLabel(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stato *</Label>
                  <Select value={formData.stato} onValueChange={(v) => setField('stato', v)}>
                    <SelectTrigger><SelectValue placeholder="Stato" /></SelectTrigger>
                    <SelectContent>
                      {statiDisponibili.map((s) => (
                        <SelectItem key={s} value={s}>{cap(s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                La password viene generata automaticamente e mostrata una sola volta dopo la creazione.
              </p>
            </div>

            {/* Note */}
            <div>
              <Label>Note</Label>
              <Input value={formData.note} onChange={(e) => setField('note', e.target.value)} />
            </div>

            <div>
              <Button onClick={salvaUtente} disabled={saving || !formData.email || !formData.nome || !formData.cognome}>
                {saving ? 'Salvataggio...' : 'Crea utente'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Input
            placeholder="Cerca per nome, cognome, email..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72"
          />
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti gli stati</SelectItem>
              {statiDisponibili.map((s) => (
                <SelectItem key={s} value={s}>{cap(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ruolo} onValueChange={setRuolo}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Ruolo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti i ruoli</SelectItem>
              {ruoliDisponibili.map((r) => (
                <SelectItem key={r} value={r}>{ruoloLabel(r)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento...</p>
          )}
          {!loading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cognome</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Ultimo accesso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {utenti.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                      Nessun utente trovato
                    </TableCell>
                  </TableRow>
                )}
                {utenti.map((u) => (
                  <TableRow
                    key={u.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/utenti/${u.id}`)}
                  >
                    <TableCell className="font-medium">{u.cognome}</TableCell>
                    <TableCell>{u.nome}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant={u.ruolo === 'admin' ? 'default' : 'secondary'}>
                        {ruoloLabel(u.ruolo)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.stato === 'attivo' ? 'default' : 'outline'}>
                        {cap(u.stato)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(u.ultimo_accesso)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UtentiList;
